/**
 * Types of feature flag values we support.
 */
declare type PrimitiveSupportedFlagTypes = boolean | string | number;
export declare type SupportedFlagTypes =
  | PrimitiveSupportedFlagTypes
  | Array<PrimitiveSupportedFlagTypes>
  | {
      [key: string]: PrimitiveSupportedFlagTypes;
    };

interface FlagSet {
  [key: string]: SupportedFlagTypes;
}

interface RecordedFeatureFlags {
  remote: FlagSet;
  overrides: FlagSet;
}

const getFromStorage = (storageKey: string) => {
  let retVal = null;
  // eslint-disable-next-line @trello/ban-identifiers
  const storage = window.localStorage;
  const rawValue = storage ? storage.getItem(storageKey) : null;

  if (rawValue) {
    try {
      retVal = JSON.parse(rawValue);
    } catch (ex) {
      console.warn(ex);
    }
  }

  return retVal;
};

/**
 * Reading recorded feature flag
 *
 * USE WITH CAUTION!!! Prefer non-falsy values.
 *
 * We are not recording 'falsy' values in snapshot (false, 0, null, '', etc). So there is a chance of:
 * * `override[key] == false` will not be picked if `remote[key] == true`
 * * if `remote[key] == false` and there is no override, **default** value will be served
 *
 * @param key
 * @param defaultValue
 * @returns
 */
export const getRecordedFeatureFlag = (
  key: string,
  defaultValue: SupportedFlagTypes,
): SupportedFlagTypes => {
  const FEATURE_FLAGS_KEY = 'featureFlagSnapshot';
  const snapshot = getFromStorage(FEATURE_FLAGS_KEY);
  const { remote = {}, overrides = {} }: RecordedFeatureFlags = snapshot ?? {};
  return overrides[key] ?? remote[key] ?? defaultValue;
};
